import { Button } from '@/components/UI/Button'
import { Dialog } from '@/components/UI/Dialog'
import { Image } from '@/components/UI/Image'
import { memo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Swiper as SwiperType } from 'swiper'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

type Props = {
  campaignName: string
  /** カルーセルに表示する画像URL配列。省略時はプレースホルダーを表示 */
  images?: string[]
  isOpen: boolean
  onClose: () => void
  onConfirm: (skipNext: boolean) => void
}

/** 初回ログイン後の確認ダイアログ */
export const DialogFirstLoginConfirm = memo(({ images, isOpen, onClose, onConfirm }: Props) => {
  const [skipNext, setSkipNext] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const swiperRef = useRef<SwiperType | null>(null)
  const { t } = useTranslation()
  const displayImages = images && images.length > 0 ? images : [null]
  const isLastSlide = activeIndex >= displayImages.length - 1

  const handleConfirm = () => {
    onConfirm(skipNext)
  }

  return (
    <Dialog selectorState={isOpen} closeFunc={onClose} showCloseButton={false} closeOnClickOutside={false}>
      <div className="flex flex-col items-center gap-4 pt-2">
        {/* コンテナ: height ベースで制約（aspect-ratio + maxHeight の組み合わせによる幅縮小バグを回避） */}
        {/* 通常端末: min(calc(100dvh - 280px), 454px) = 454px、iPhone SE: 387px */}
        <div
          className="relative w-full bg-gray-50 rounded-lg overflow-hidden"
          style={{ height: 'min(calc(100dvh - 280px), 454px)' }}
        >
          <Swiper
            modules={displayImages.length > 1 ? [Pagination] : []}
            pagination={displayImages.length > 1 ? { clickable: true } : false}
            spaceBetween={0}
            slidesPerView={1}
            style={{ width: '100%', height: '100%' }}
            className={`rounded-lg${displayImages.length > 1 ? ' pb-8' : ''}`}
            onSwiper={(swiper) => {
              swiperRef.current = swiper
            }}
            onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
          >
            {displayImages.map((src, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: 画像の順序が変わらないため index を key に使用
              <SwiperSlide key={i}>
                {src ? (
                  <Image
                    src={src}
                    alt={`${t('home.campaign_intro', 'キャンペーン紹介')} ${i + 1}`}
                    fit="contain-natural"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">No Image</div>
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* 課題2・3: 常に DOM に存在させ、isLastSlide でない場合は invisible で非表示（高さ確保） */}
        <label className={`flex items-center gap-2 cursor-pointer pl-4 w-full ${isLastSlide ? '' : 'invisible'}`}>
          <input
            type="checkbox"
            checked={skipNext}
            onChange={(e) => setSkipNext(e.target.checked)}
            className="w-6 h-6 border-2 border-black rounded-none cursor-pointer"
            disabled={!isLastSlide}
            tabIndex={isLastSlide ? 0 : -1}
          />
          <span className="text-[15px] text-black">{t('common.popup.hide', '次回から表示しない')}</span>
        </label>

        {/* 最後のスライドのみ「閉じる」、それ以外は「次へ」 */}
        <div className="flex justify-center w-full">
          {isLastSlide ? (
            <Button
              label={t('common.close', '閉じる')}
              onClick={handleConfirm}
              size="sm"
              fullWidth
              className="!rounded-lg"
            />
          ) : (
            <Button
              label={t('common.next', '次へ')}
              onClick={() => swiperRef.current?.slideNext()}
              size="sm"
              fullWidth
              className="!rounded-lg"
            />
          )}
        </div>
      </div>
    </Dialog>
  )
})
